import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  RiCloseLine,
  RiUploadCloud2Line,
  RiDeleteBinLine,
  RiCheckboxCircleFill,
  RiImageLine,
  RiSearchLine,
  RiArrowLeftLine,
  RiArrowRightLine,
} from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/Drawer';
import TabStrip from '@components/ui/TabStrip';
import Switch from '@components/ui/Switch';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import SelectOperatorModal from './SelectOperatorModal';
import LocationInput from './LocationInput';
import { getAircraftBundledCarouselUrls } from '@assets/images';
import MakeModelSearchDialog from './manual-aircraft/MakeModelSearchDialog';
import AircraftPreviewDrawer from './manual-aircraft/AircraftPreviewDrawer';
import SaveRetryDialog from './manual-aircraft/SaveRetryDialog';
import { useAddAircraftSaveMachine, isSameSessionMakeModel } from './manual-aircraft/useAddAircraftSaveMachine';

const MAX_PHOTOS = 10;
const MAX_FILE_MB = 10;
const DESCRIPTION_LIMIT = 500;

// Enum values are locked in this order to match the BE:
//   aircraft_status  → AVAILABLE / OCCUPIED / MAINTENANCE / AOG / INACTIVE
//   aircraft_tier    → BASIC / SILVER / GOLD / PLATINUM
const STATUS_OPTIONS = ['AVAILABLE', 'OCCUPIED', 'MAINTENANCE', 'AOG', 'INACTIVE'];
const TIER_OPTIONS = ['BASIC', 'SILVER', 'GOLD', 'PLATINUM'];

const TABS = [
  { key: 'model',    label: 'Model & Operator' },
  { key: 'aircraft', label: 'Aircraft' },
  { key: 'extras',   label: 'Features & Photos' },
];

// ── Small labeled field primitives (kept local — they only exist here) ────
const LabeledInput = ({ label, required, hint, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-primary-500"> *</span>}
    </label>
    <input
      {...props}
      className={`w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${props.className || ''}`}
    />
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const LabeledSelect = ({ label, required, value, onChange, options, ...rest }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-primary-500"> *</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      {...rest}
    >
      {options.map((o) => (
        <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
          {typeof o === 'string' ? o : o.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * Chip input — comma or Enter commits a token; backspace on empty removes
 * the last chip. Used for both custom_features and additional_amenities.
 */
const ChipInput = ({ label, values = [], onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="min-h-[44px] w-full px-2 py-1.5 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent flex flex-wrap gap-1.5 items-center">
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
            {v}
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="text-gray-400 hover:text-red-500"
            ><RiCloseLine className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
            else if (e.key === 'Backspace' && !draft && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={values.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[8ch] py-1 text-sm bg-transparent outline-none placeholder-gray-400"
        />
      </div>
      <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add.</p>
    </div>
  );
};

/**
 * AddAircraftManuallyDrawer — 3-tab wizard for creating a new
 * organization-scoped aircraft during quote creation. On the final tab
 * the Next button becomes Preview → opens AircraftPreviewDrawer → Save
 * runs the six-step chained-persist state machine in
 * useAddAircraftSaveMachine. On success the fully-hydrated aircraft is
 * pushed to the current Quick Quote shortlist via appStore.addToShortlist.
 */
const AddAircraftManuallyDrawer = ({
  isOpen = false,
  onClose,
  // Presence of initialAircraft flips the wizard into EDIT mode:
  //   - hydration prefills every form slice from the record
  //   - all tab strips start unlocked (existing data is already valid)
  //   - Save uses PATCH endpoints in steps 2/3/4 and runs step 7's
  //     S3 cleanup diff (deletes commented until BE endpoint lands)
  //   - onSaved(finalAircraft) fires instead of addToShortlist
  initialAircraft = null,
  onSaved,
  // Create-mode override: when supplied, the drawer calls
  // `onCreated(hydratedAircraft)` on success instead of pushing into the
  // Quick Quote shortlist. Used by edit/duplicate/trip-group contexts on
  // GenerateQuote so the new tail lands directly on formData.bidOptions
  // (reconcileBidOptions POSTs it on the next preview-save).
  onCreated,
}) => {
  const { addToShortlist } = useAppStore();
  const { userProfile } = useAuthStore();
  const orgId = userProfile?.org_id;
  const isEditMode = !!initialAircraft;

  // ── Form state ──────────────────────────────────────────────────────────
  const emptyForm = () => ({
    aircraft: {
      // NOTE: manufacturer, aircraft_type, AND model are NOT stored on
      // the aircraft slice — Tab 2's makeModel is the single source of
      // truth. The save-payload builder mirrors makeModel.model_name
      // into the aircraft body's `model` column at save time.
      tail_number: '',
      year: '',
      serial_number: '',
      max_passengers: '',
      range_nm: '',
      cruise_speed_kts: '',
      current_status: 'AVAILABLE',
      last_maintenance: '',
      next_maintenance_due: '',
      insurance_expiry: '',
      home_base: '',
      hourly_rate: '',
      aw_date: '',
      registered_owner: '',
      registered_owner_state: '',
      fractional_owner: false,
    },
    makeModel: {
      make_code: '',
      model_code: '',
      manufacturer: '',
      model_name: '',
      aircraft_type: '',
      min_range: '',
      max_range: '',
    },
    features: { tier: 'BASIC', has_wingman: false, no_aog: false, custom_features: [] },
    amenities: {
      has_wifi: false, has_entertainment_system: false, has_full_galley: false,
      has_enclosed_lavatory: false, has_satellite_phone: false, additional_amenities: [],
    },
    description: '',
    photos: [],
    operator: null,
    selectedMakeModelRow: null,   // row picked from search — used for dirty detect
  });
  const [form, setForm] = useState(emptyForm());
  const setAircraft = (patch) => setForm((p) => ({ ...p, aircraft: { ...p.aircraft, ...patch } }));
  const setMakeModel = (patch) => setForm((p) => ({ ...p, makeModel: { ...p.makeModel, ...patch } }));
  const setFeatures = (patch) => setForm((p) => ({ ...p, features: { ...p.features, ...patch } }));
  const setAmenities = (patch) => setForm((p) => ({ ...p, amenities: { ...p.amenities, ...patch } }));

  // ── Wizard state ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('model');
  // maxReachedTab = the highest-index tab whose Next has been clicked at
  // least once. Tabs beyond this are click-disabled until Next fires.
  const [maxReachedTab, setMaxReachedTab] = useState(0);

  const [operatorPickerOpen, setOperatorPickerOpen] = useState(false);
  const [makeModelSearchOpen, setMakeModelSearchOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  // Fires once per open when initialAircraft is provided — prevents the
  // hydration effect from re-firing on every form-state update.
  const hydratedRef = useRef(false);
  // Precomputed in the hydration step: does the aircraft + its
  // make_model share a wizard session (within SAME_SESSION_MS)? Feeds
  // the save-payload builder so the machine picks PATCH vs POST-new.
  const sessionPatchOkRef = useRef(false);

  // ── Reset on close so a fresh open starts clean ─────────────────────────
  useEffect(() => {
    if (isOpen) return;
    form.photos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setForm(emptyForm());
    setActiveTab('model');
    setMaxReachedTab(0);
    setPreviewOpen(false);
    hydratedRef.current = false;
    sessionPatchOkRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Edit-mode hydration ─────────────────────────────────────────────────
  // Runs exactly once per open when initialAircraft is present. Fills
  // every form slice from the record and unlocks all tabs so the broker
  // can jump between them freely (existing data is already valid).
  useEffect(() => {
    if (!isOpen || !initialAircraft || hydratedRef.current) return;
    hydratedRef.current = true;

    const a = initialAircraft;
    const mm = a.aircraft_make_model || {};
    const feats = a.aircraft_features || {};
    const am = a.aircraft_amenities || {};

    // Prefill photo rows as `existing` — save-machine step 5 skips S3
    // upload for these and passes their saveUrl straight through. Keep
    // full URL as previewUrl for local <img> resolution.
    const initialPhotos = Array.isArray(a.image_urls)
      ? a.image_urls
          .filter(Boolean)
          .map((url) => ({
            kind: 'existing',
            name: 'Existing photo',
            size: 0,
            previewUrl: url,
            saveUrl: url,
          }))
      : [];

    setForm({
      aircraft: {
        tail_number: a.tail_number || '',
        year: a.year != null ? String(a.year) : (a.mfr_year != null ? String(a.mfr_year) : ''),
        serial_number: a.serial_number || '',
        max_passengers: a.max_passengers != null
          ? String(a.max_passengers)
          : (a.passenger_capacity != null ? String(a.passenger_capacity) : ''),
        range_nm: a.range_nm != null
          ? String(a.range_nm)
          : (a.range_nautical_miles != null ? String(a.range_nautical_miles) : ''),
        cruise_speed_kts: a.cruise_speed_kts != null
          ? String(a.cruise_speed_kts)
          : (a.cruise_speed_knots != null ? String(a.cruise_speed_knots) : ''),
        current_status: a.current_status || 'AVAILABLE',
        last_maintenance: a.last_maintenance ? a.last_maintenance.slice(0, 10) : '',
        next_maintenance_due: a.next_maintenance_due ? a.next_maintenance_due.slice(0, 10) : '',
        insurance_expiry: a.insurance_expiry ? a.insurance_expiry.slice(0, 10) : '',
        aw_date: a.aw_date ? a.aw_date.slice(0, 10) : '',
        home_base: a.home_base || '',
        hourly_rate: a.hourly_rate != null ? String(a.hourly_rate) : '',
        registered_owner: a.registered_owner || '',
        registered_owner_state: a.registered_owner_state || '',
        fractional_owner: !!a.fractional_owner,
      },
      makeModel: {
        make_code: mm.make_code || '',
        model_code: mm.model_code || '',
        manufacturer: mm.manufacturer || '',
        // Fall back to aircraft.model when the make/model row isn't
        // joined but the aircraft column has a value — legacy rows /
        // JetNet-imports may only populate one side.
        model_name: mm.model_name || a.model || '',
        aircraft_type: mm.aircraft_type || '',
        min_range: mm.min_range != null ? String(mm.min_range) : '',
        max_range: mm.max_range != null ? String(mm.max_range) : '',
      },
      features: {
        tier: feats.tier || 'BASIC',
        has_wingman: !!feats.has_wingman,
        no_aog: !!feats.no_aog,
        custom_features: Array.isArray(feats.custom_features) ? feats.custom_features : [],
      },
      amenities: {
        has_wifi: !!am.has_wifi,
        has_entertainment_system: !!am.has_entertainment_system,
        has_full_galley: !!am.has_full_galley,
        has_enclosed_lavatory: !!am.has_enclosed_lavatory,
        has_satellite_phone: !!am.has_satellite_phone,
        additional_amenities: Array.isArray(am.additional_amenities)
          ? am.additional_amenities
          : (typeof am.additional_amenities === 'string'
              ? am.additional_amenities.split(',').map((s) => s.trim()).filter(Boolean)
              : []),
      },
      description: a.description || '',
      photos: initialPhotos,
      operator: a.operator || (a.operator_id ? { operator_id: a.operator_id, name: '' } : null),
      // Stash the FULL row so dirty-detection at save time compares
      // against the persisted values. Includes aircraft_make_model_id
      // so step 1 knows which row to PATCH/reuse.
      selectedMakeModelRow: mm.aircraft_make_model_id ? mm : null,
    });

    // Precompute the PATCH-vs-POST-new hint for step 1.
    sessionPatchOkRef.current = isSameSessionMakeModel(a.created_at, mm.created_at);

    // Unlock every tab — existing data is already valid.
    setMaxReachedTab(TABS.length - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialAircraft]);

  // ── Validation per tab ──────────────────────────────────────────────────
  // Tab 1 — Model & Operator: identity fields. Tail number lives here
  // now (moved from the Aircraft tab) so all "who is this aircraft"
  // decisions are locked in before broker moves to the specs step.
  const isTab1Valid = useMemo(() => {
    const m = form.makeModel;
    return !!(
      form.aircraft.tail_number.trim()
      && form.operator?.operator_id
      && m.make_code.trim()
      && m.model_code.trim()
      && m.manufacturer.trim()
      && m.model_name.trim()
      && m.aircraft_type.trim()
    );
  }, [form.aircraft.tail_number, form.makeModel, form.operator]);

  // Tab 2 — Aircraft: specs. Tail number required-check moved out to
  // Tab 1 above.
  const isTab2Valid = useMemo(() => {
    const a = form.aircraft;
    return !!(
      String(a.max_passengers).trim()
      && a.current_status
    );
  }, [form.aircraft]);

  const isTab3Valid = useMemo(() => {
    return !!(form.features.tier && form.photos.length >= 1);
  }, [form.features.tier, form.photos.length]);

  // Auto-demote maxReachedTab if the user backtracks and invalidates
  // an earlier tab — locks the future tabs again until they re-Next.
  useEffect(() => {
    if (maxReachedTab >= 1 && !isTab1Valid) setMaxReachedTab(0);
  }, [isTab1Valid, maxReachedTab]);
  useEffect(() => {
    if (maxReachedTab >= 2 && !isTab2Valid) setMaxReachedTab(1);
  }, [isTab2Valid, maxReachedTab]);

  const tabs = useMemo(() => TABS.map((t, i) => ({
    ...t,
    disabled: i > maxReachedTab,
  })), [maxReachedTab]);

  const activeIndex = TABS.findIndex((t) => t.key === activeTab);
  const currentTabValid =
    activeIndex === 0 ? isTab1Valid
    : activeIndex === 1 ? isTab2Valid
    : isTab3Valid;

  const handleNext = () => {
    if (!currentTabValid) return;
    if (activeIndex === TABS.length - 1) {
      // Last tab — Preview instead of Next
      setPreviewOpen(true);
      return;
    }
    setMaxReachedTab((m) => Math.max(m, activeIndex + 1));
    setActiveTab(TABS[activeIndex + 1].key);
  };
  const handleBack = () => {
    if (activeIndex === 0) return;
    setActiveTab(TABS[activeIndex - 1].key);
  };

  // ── Photos ──────────────────────────────────────────────────────────────
  const handlePickPhotos = (fileList) => {
    const raw = Array.from(fileList || []);
    if (raw.length === 0) return;
    const accepted = raw.filter((f) => {
      const okType = /^image\/(jpeg|jpg|png)$/i.test(f.type);
      const okSize = f.size <= MAX_FILE_MB * 1024 * 1024;
      if (!okType) toast.error(`${f.name}: JPEG/PNG only.`);
      else if (!okSize) toast.error(`${f.name}: exceeds ${MAX_FILE_MB} MB.`);
      return okType && okSize;
    });
    if (accepted.length === 0) return;
    const remaining = MAX_PHOTOS - form.photos.length;
    if (remaining <= 0) { toast.error(`Max ${MAX_PHOTOS} photos.`); return; }
    const toAdd = accepted.slice(0, remaining);
    if (accepted.length > remaining) toast.error(`Only ${remaining} photo slot(s) left.`);
    setForm((p) => ({
      ...p,
      photos: [
        ...p.photos,
        ...toAdd.map((file) => ({
          kind: 'new',      // File → step 5 uploads to S3
          file, name: file.name, size: file.size, previewUrl: URL.createObjectURL(file),
        })),
      ],
    }));
  };
  const handleRemovePhoto = (index) => setForm((p) => {
    const next = [...p.photos];
    const [removed] = next.splice(index, 1);
    // Only blob previewUrls need revoking. Bundled 'existing' URLs are
    // stable public paths, no-op if revoked but skip to be explicit.
    if (removed?.kind !== 'existing' && removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    return { ...p, photos: next };
  });

  // ── Bundled fallback suggestions (same asset lookup EditPricingDrawer
  // uses). Keyed off the wizard's picked make/model codes — first tries
  // "{MAKE}-{MODEL}" (e.g. "CE-550-550"), then bare model_code. Empty
  // when the app has no bundled photos for this model. Bundled URLs
  // are stable /images/aircraft/… paths — the save machine treats them
  // as `existing` (skips S3 upload, passes through into image_urls).
  const bundledSuggestions = useMemo(() => {
    const mk = (form.makeModel.make_code || '').trim().toUpperCase();
    const md = (form.makeModel.model_code || '').trim().toUpperCase();
    if (!md) return [];
    const combo = mk ? `${mk}-${md}` : '';
    const first = combo ? getAircraftBundledCarouselUrls(combo) : [];
    if (first.length > 0) return first;
    return getAircraftBundledCarouselUrls(md) || [];
  }, [form.makeModel.make_code, form.makeModel.model_code]);

  // Hide any suggestion the broker has already picked.
  const usedSuggestionUrls = useMemo(
    () => new Set(form.photos.filter((p) => p.kind === 'existing').map((p) => p.previewUrl)),
    [form.photos],
  );
  const availableSuggestions = useMemo(
    () => bundledSuggestions.filter((url) => !usedSuggestionUrls.has(url)),
    [bundledSuggestions, usedSuggestionUrls],
  );

  // Strip the runtime origin from a bundled URL so the DB stores a
  // relative path (e.g. "/images/aircraft/BE-58/BE-58.png") instead of
  // an environment-baked absolute one ("http://localhost:3000/images/…").
  // formatImagUrl re-adds window.location.origin on the read side for
  // /images/* paths, so display keeps working across dev / staging / prod.
  const toRelativeSaveUrl = (url) => {
    if (!url) return url;
    const origin = window.location.origin;
    return url.startsWith(origin) ? url.slice(origin.length) : url;
  };

  const handleAddSuggestion = (url) => {
    if (!url) return;
    if (form.photos.length >= MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos.`); return; }
    setForm((p) => ({
      ...p,
      photos: [
        ...p.photos,
        // kind:'existing' → save-machine step 5 passes saveUrl through
        // into image_urls[] unchanged (no S3 upload). previewUrl stays
        // as the full URL so the local <img> tag can resolve it now.
        {
          kind: 'existing',
          name: 'Suggested photo',
          size: 0,
          previewUrl: url,
          saveUrl: toRelativeSaveUrl(url),
        },
      ],
    }));
  };
  const handleAddAllSuggestions = () => {
    if (availableSuggestions.length === 0) return;
    const remaining = MAX_PHOTOS - form.photos.length;
    if (remaining <= 0) { toast.error(`Max ${MAX_PHOTOS} photos.`); return; }
    const toAdd = availableSuggestions.slice(0, remaining);
    setForm((p) => ({
      ...p,
      photos: [
        ...p.photos,
        ...toAdd.map((url) => ({
          kind: 'existing',
          name: 'Suggested photo',
          size: 0,
          previewUrl: url,
          saveUrl: toRelativeSaveUrl(url),
        })),
      ],
    }));
  };

  // ── Make/Model row-pick + dirty detect ──────────────────────────────────
  const handleMakeModelPick = (row) => {
    setMakeModel({
      make_code: row.make_code || '',
      model_code: row.model_code || '',
      manufacturer: row.manufacturer || '',
      model_name: row.model_name || '',
      aircraft_type: row.aircraft_type || '',
      min_range: row.min_range?.toString() || '',
      max_range: row.max_range?.toString() || '',
    });
    // No aircraft-side mirror needed — makeModel is the single source
    // of truth for manufacturer, aircraft_type, AND model_name now.
    // buildSavePayload copies model_name into the aircraft body at save.
    setForm((p) => ({ ...p, selectedMakeModelRow: row }));
  };

  // Dirty = user picked a row AND has edited any of the make/model fields
  // since the pick. Also dirty when no row was ever picked (always new).
  const isMakeModelDirty = useMemo(() => {
    const row = form.selectedMakeModelRow;
    if (!row) return true;   // no row selected → always POST a new one
    const m = form.makeModel;
    return !(
      (row.make_code || '') === m.make_code
      && (row.model_code || '') === m.model_code
      && (row.manufacturer || '') === m.manufacturer
      && (row.model_name || '') === m.model_name
      && (row.aircraft_type || '') === m.aircraft_type
      && String(row.min_range ?? '') === String(m.min_range ?? '')
      && String(row.max_range ?? '') === String(m.max_range ?? '')
    );
  }, [form.selectedMakeModelRow, form.makeModel]);

  // ── Save state machine ──────────────────────────────────────────────────
  const { run, retry, reset: resetSave, isRunning, error: saveError, success } =
    useAddAircraftSaveMachine({ organizationId: orgId });

  const buildSavePayload = useCallback(() => {
    const a = form.aircraft;
    const numOrNull = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const dateOrNull = (v) => (v ? new Date(v).toISOString() : null);

    return {
      mode: isEditMode ? 'edit' : 'create',
      editing: isEditMode ? {
        aircraftId: initialAircraft.aircraft_id,
        // Existence flags gate step 3/4 between POST and PATCH so we
        // don't try to PATCH a sub-row that was never created.
        hadFeatures: !!initialAircraft.aircraft_features?.aircraft_feature_id,
        hadAmenities: !!initialAircraft.aircraft_amenities?.aircraft_amenity_id,
        // Snapshot of the persisted image_urls for step 7's diff (S3
        // cleanup — currently commented until BE endpoint ships).
        originalImageUrls: Array.isArray(initialAircraft.image_urls)
          ? initialAircraft.image_urls
          : [],
      } : null,
      makeModel: {
        id: form.selectedMakeModelRow?.aircraft_make_model_id || null,
        // Save-machine reads `isDirty`; keep `isNew` too for backward
        // compat (older mental model in the file's comments).
        isDirty: isMakeModelDirty,
        isNew: isMakeModelDirty,
        sessionPatchOk: sessionPatchOkRef.current,
        body: {
          make_code: form.makeModel.make_code.trim(),
          model_code: form.makeModel.model_code.trim(),
          manufacturer: form.makeModel.manufacturer.trim(),
          model_name: form.makeModel.model_name.trim(),
          aircraft_type: form.makeModel.aircraft_type.trim(),
          min_range: numOrNull(form.makeModel.min_range),
          max_range: numOrNull(form.makeModel.max_range),
        },
      },
      aircraft: {
        body: {
          tail_number: a.tail_number.trim(),
          make_model: form.makeModel.model_name.trim(),
          operator_id: form.operator?.operator_id || null,
          extra_attributes: form.operator?.operator_id
            ? {}
            : { operator_name: form.operator?.name || '' },
          year: numOrNull(a.year),
          mfr_year: numOrNull(a.year),  // send both — same value
          serial_number: a.serial_number.trim() || null,
          // manufacturer + aircraft_type live only on Tab 2's makeModel
          // slice now — mirror to the aircraft columns at save time.
          aircraft_type: form.makeModel.aircraft_type.trim(),
          manufacturer: form.makeModel.manufacturer.trim(),
          passenger_capacity: numOrNull(a.max_passengers),
          max_passengers: numOrNull(a.max_passengers),  // duplicated
          range_nautical_miles: numOrNull(a.range_nm),
          range_nm: numOrNull(a.range_nm),
          cruise_speed_knots: numOrNull(a.cruise_speed_kts),
          cruise_speed_kts: numOrNull(a.cruise_speed_kts),
          current_status: a.current_status,
          last_maintenance: dateOrNull(a.last_maintenance),
          next_maintenance_due: dateOrNull(a.next_maintenance_due),
          insurance_expiry: dateOrNull(a.insurance_expiry),
          aw_date: dateOrNull(a.aw_date),
          home_base: a.home_base.trim() || null,
          hourly_rate: numOrNull(a.hourly_rate),
          registered_owner: a.registered_owner.trim() || null,
          registered_owner_state: a.registered_owner_state.trim() || null,
          fractional_owner: !!a.fractional_owner,
          // Mirror makeModel.model_name into the aircraft's `model`
          // column — Tab 2 owns the value, aircraft column still
          // needs to be populated for the POST/PATCH.
          model: form.makeModel.model_name.trim(),
          description: form.description.trim() || null,
        },
      },
      features: {
        body: {
          tier: form.features.tier,
          has_wingman: !!form.features.has_wingman,
          no_aog: !!form.features.no_aog,
          custom_features: form.features.custom_features,
        },
      },
      amenities: {
        body: {
          has_wifi: !!form.amenities.has_wifi,
          has_entertainment_system: !!form.amenities.has_entertainment_system,
          has_full_galley: !!form.amenities.has_full_galley,
          has_enclosed_lavatory: !!form.amenities.has_enclosed_lavatory,
          has_satellite_phone: !!form.amenities.has_satellite_phone,
          additional_amenities: form.amenities.additional_amenities,
        },
      },
      photos: form.photos,
      tailNumber: a.tail_number.trim(),
    };
  }, [form, isMakeModelDirty, isEditMode, initialAircraft]);

  const handleSaveFromPreview = () => {
    resetSave();
    run(buildSavePayload());
  };

  // ── On save success ─────────────────────────────────────────────────────
  // Create mode → append to shortlist (Quick Quote flow).
  // Edit mode   → hand back the updated aircraft via onSaved; parent
  //               splices it into its list. Details drawer stays closed.
  useEffect(() => {
    if (!success) return;
    const aircraftId = success.aircraft_id;
    const manufacturer = form.makeModel.manufacturer.trim();
    // Build a fully-hydrated aircraft record from local form state +
    // whatever the final PATCH response returned. Used for BOTH
    // shortlist entry and edit onSaved handoff so consumers see one
    // consistent shape.
    const hydratedAircraft = {
      ...(initialAircraft || {}),
      ...success,
      aircraft_id: aircraftId,
      tail_number: form.aircraft.tail_number.trim(),
      make_model: form.makeModel.model_name.trim(),
      manufacturer,
      model: form.makeModel.model_name.trim(),
      image_urls: success.image_urls || [],
      banner_image_url: success.banner_image_url || null,
      aircraft_features: {
        tier: form.features.tier,
        no_aog: form.features.no_aog,
        has_wingman: form.features.has_wingman,
        custom_features: form.features.custom_features,
      },
      aircraft_amenities: {
        has_wifi: form.amenities.has_wifi,
        has_entertainment_system: form.amenities.has_entertainment_system,
        has_full_galley: form.amenities.has_full_galley,
        has_enclosed_lavatory: form.amenities.has_enclosed_lavatory,
        has_satellite_phone: form.amenities.has_satellite_phone,
        additional_amenities: form.amenities.additional_amenities,
      },
      description: form.description.trim() || null,
      operator: form.operator,
      operator_id: form.operator?.operator_id || null,
    };

    if (isEditMode) {
      onSaved?.(hydratedAircraft);
      toast.success('Aircraft updated.');
    } else if (onCreated) {
      // Edit / duplicate / trip-group flows on GenerateQuote — the parent
      // appends this straight to formData.bidOptions. Skip the shortlist
      // (irrelevant when the quote's tail list isn't sourced from it).
      onCreated(hydratedAircraft);
      toast.success('Aircraft created and added to quote.');
    } else {
      const shortlistEntry = {
        aircraft_id: aircraftId,
        tail_number: form.aircraft.tail_number.trim(),
        make_model: form.makeModel.model_name.trim(),
        manufacturer,
        model: form.makeModel.model_name.trim(),
        description: form.description.trim() || null,
        image_urls: success.image_urls || [],
        banner_image_url: success.banner_image_url || null,
        aircraft_features: hydratedAircraft.aircraft_features,
        aircraft: hydratedAircraft,
        operator: form.operator,
        operator_id: form.operator?.operator_id || null,
      };
      addToShortlist(shortlistEntry);
      toast.success('Aircraft created and added to quote.');
    }

    setPreviewOpen(false);
    resetSave();
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <>
      {/* modal={false} — releases Radix Dialog's FocusScope so the nested
          SelectOperatorModal / MakeModelSearchDialog inputs (portalled to
          document.body as siblings) can actually receive focus. Overlay
          and z-order are unaffected. Outside-click closure is separately
          disabled below via closeOnOutsideClick={false}. */}
      <Drawer open={isOpen} onOpenChange={onClose} modal={false}>
        <DrawerContent
          side="right"
          className="w-full !max-w-2xl flex flex-col p-0"
          hideCloseButton
          closeOnOutsideClick={false}
        >
          <DrawerHeader className="sr-only">
            <DrawerTitle>{isEditMode ? 'Edit Aircraft' : 'Add Aircraft Manually'}</DrawerTitle>
          </DrawerHeader>

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h2 className="text-2xl font-semibold text-gray-900">{isEditMode ? 'Edit Aircraft' : 'Add Aircraft Manually'}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <RiCloseLine className="w-4 h-4" />
            </button>
          </div>

          {/* Tab strip */}
          <TabStrip tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {activeTab === 'aircraft' && (
              <div className="space-y-6">
                {/* Basics — required fields lead the tab; Home Base sits
                    directly under them since it's the third "operational
                    where/how" datum a broker reaches for after status. */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Basics</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Max Passengers" required type="number" min="0"
                        value={form.aircraft.max_passengers}
                        onChange={(e) => setAircraft({ max_passengers: e.target.value })}
                        placeholder="6"
                      />
                      <LabeledSelect
                        label="Current Status" required
                        value={form.aircraft.current_status}
                        onChange={(v) => setAircraft({ current_status: v })}
                        options={STATUS_OPTIONS}
                      />
                    </div>
                    <LocationInput
                      label="Home Base"
                      value={form.aircraft.home_base}
                      onChange={(airport) => setAircraft({
                        home_base: airport?.icao || airport?.code || '',
                      })}
                      placeholder="Search airport"
                    />
                  </div>
                </section>

                {/* Manufacture — how the aircraft came into the world. */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Manufacture</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <LabeledInput
                      label="Year" type="number" min="1900" max="2100"
                      value={form.aircraft.year}
                      onChange={(e) => setAircraft({ year: e.target.value })}
                      placeholder="2020"
                      hint="Also stored as mfr_year"
                    />
                    <LabeledInput
                      label="Serial Number"
                      value={form.aircraft.serial_number}
                      onChange={(e) => setAircraft({ serial_number: e.target.value })}
                    />
                  </div>
                </section>

                {/* Performance — physical + commercial capacity. Hourly
                    Rate lives here since it's the commercial-side spec
                    that pairs with the physical capacity numbers. */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Performance</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <LabeledInput
                      label="Range (nm)" type="number" min="0"
                      value={form.aircraft.range_nm}
                      onChange={(e) => setAircraft({ range_nm: e.target.value })}
                    />
                    <LabeledInput
                      label="Cruise (kts)" type="number" min="0"
                      value={form.aircraft.cruise_speed_kts}
                      onChange={(e) => setAircraft({ cruise_speed_kts: e.target.value })}
                    />
                    <LabeledInput
                      label="Hourly Rate ($)" type="number" min="0"
                      value={form.aircraft.hourly_rate}
                      onChange={(e) => setAircraft({ hourly_rate: e.target.value })}
                    />
                  </div>
                </section>

                {/* Maintenance & Compliance — regulatory + upkeep dates
                    grouped since they share the same date-picker UX and
                    the same broker mental model ("what's expiring soon"). */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Maintenance &amp; Compliance</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Last Maintenance" type="date"
                        value={form.aircraft.last_maintenance}
                        onChange={(e) => setAircraft({ last_maintenance: e.target.value })}
                      />
                      <LabeledInput
                        label="Next Maintenance Due" type="date"
                        value={form.aircraft.next_maintenance_due}
                        onChange={(e) => setAircraft({ next_maintenance_due: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Insurance Expiry" type="date"
                        value={form.aircraft.insurance_expiry}
                        onChange={(e) => setAircraft({ insurance_expiry: e.target.value })}
                      />
                      <LabeledInput
                        label="Airworthiness Date" type="date"
                        value={form.aircraft.aw_date}
                        onChange={(e) => setAircraft({ aw_date: e.target.value })}
                      />
                    </div>
                  </div>
                </section>

                {/* Ownership — legal + fractional. Toggle sits under the
                    owner name/state pair since it's a modifier on that
                    same fact ("does the named owner own it outright?"). */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Ownership</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Registered Owner"
                        value={form.aircraft.registered_owner}
                        onChange={(e) => setAircraft({ registered_owner: e.target.value })}
                      />
                      <LabeledInput
                        label="Owner State"
                        value={form.aircraft.registered_owner_state}
                        onChange={(e) => setAircraft({ registered_owner_state: e.target.value.toUpperCase() })}
                        maxLength={2}
                        placeholder="NJ"
                      />
                    </div>
                    <div className="border border-gray-100 rounded-xl p-3">
                      <Switch
                        label="Fractional Ownership"
                        description="Aircraft has fractional ownership"
                        checked={form.aircraft.fractional_owner}
                        onChange={(v) => setAircraft({ fractional_owner: v })}
                      />
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'model' && (
              <div className="space-y-4">
                {/* Tail Number leads Tab 1 — the aircraft's real-world
                    identifier is the first thing a broker types when
                    creating a new record. */}
                <LabeledInput
                  label="Tail Number" required
                  value={form.aircraft.tail_number}
                  onChange={(e) => setAircraft({ tail_number: e.target.value.toUpperCase().slice(0, 10) })}
                  placeholder="N123B"
                  maxLength={10}
                  hint="Max 10 characters."
                />

                {/* Operator picker (reuse existing modal) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Operator <span className="text-primary-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setOperatorPickerOpen(true)}
                    className={`w-full px-3 py-2.5 border rounded-xl text-sm text-left transition-colors ${
                      form.operator ? 'border-gray-200 text-gray-900 hover:bg-gray-50' : 'border-gray-200 text-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    {form.operator?.name || 'Select operator…'}
                  </button>
                  {form.operator && (form.operator.faa_city || form.operator.faa_state) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {form.operator.faa_city}
                      {form.operator.faa_state && `, ${form.operator.faa_state}`}
                    </p>
                  )}
                </div>

                {/* Make/Model search */}
                <div className="border border-gray-100 rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">Search existing make/model</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Pick a row to auto-fill the fields below. Edits after picking create a new record on save.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMakeModelSearchOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <RiSearchLine className="w-3.5 h-3.5" /> Search
                  </button>
                </div>

                {form.selectedMakeModelRow && (
                  <div className={`text-xs px-3 py-2 rounded-lg ${isMakeModelDirty ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-green-50 text-green-800 border border-green-200'}`}>
                    {isMakeModelDirty
                      ? 'Selected row was edited — a new make/model record will be created on save.'
                      : 'Selected row will be reused as-is on save.'}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Make Code" required
                    value={form.makeModel.make_code}
                    onChange={(e) => setMakeModel({ make_code: e.target.value.toUpperCase() })}
                    placeholder="DASSAULT"
                  />
                  <LabeledInput
                    label="Model Code" required
                    value={form.makeModel.model_code}
                    onChange={(e) => setMakeModel({ model_code: e.target.value.toUpperCase() })}
                    placeholder="FALCON"
                  />
                </div>
                <LabeledInput
                  label="Manufacturer" required
                  value={form.makeModel.manufacturer}
                  onChange={(e) => setMakeModel({ manufacturer: e.target.value })}
                  placeholder="Dassault"
                />
                <LabeledInput
                  label="Model Name" required
                  value={form.makeModel.model_name}
                  onChange={(e) => setMakeModel({ model_name: e.target.value })}
                  placeholder="Falcon 8X"
                />
                <LabeledInput
                  label="Aircraft Type" required
                  value={form.makeModel.aircraft_type}
                  onChange={(e) => setMakeModel({ aircraft_type: e.target.value })}
                  placeholder="Fixed-wing Multi-engine (Jet)"
                />
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput
                    label="Min Range (nm)" type="number" min="0"
                    value={form.makeModel.min_range}
                    onChange={(e) => setMakeModel({ min_range: e.target.value })}
                  />
                  <LabeledInput
                    label="Max Range (nm)" type="number" min="0"
                    value={form.makeModel.max_range}
                    onChange={(e) => setMakeModel({ max_range: e.target.value })}
                  />
                </div>
              </div>
            )}

            {activeTab === 'extras' && (
              <div className="space-y-6">
                {/* Features */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Features</h3>
                  <div className="space-y-3">
                    <LabeledSelect
                      label="Tier" required
                      value={form.features.tier}
                      onChange={(v) => setFeatures({ tier: v })}
                      options={TIER_OPTIONS}
                    />
                    <div className="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100">
                      <Switch label="Wingman certified" checked={form.features.has_wingman} onChange={(v) => setFeatures({ has_wingman: v })} />
                      <Switch label="No AOG history" checked={form.features.no_aog} onChange={(v) => setFeatures({ no_aog: v })} />
                    </div>
                    <ChipInput
                      label="Custom Features"
                      values={form.features.custom_features}
                      onChange={(v) => setFeatures({ custom_features: v })}
                      placeholder="Add a feature and press Enter"
                    />
                  </div>
                </section>

                {/* Amenities */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Amenities</h3>
                  <div className="border border-gray-100 rounded-xl px-3 divide-y divide-gray-100">
                    <Switch label="Wi-Fi" checked={form.amenities.has_wifi} onChange={(v) => setAmenities({ has_wifi: v })} />
                    <Switch label="Entertainment system" checked={form.amenities.has_entertainment_system} onChange={(v) => setAmenities({ has_entertainment_system: v })} />
                    <Switch label="Full galley" checked={form.amenities.has_full_galley} onChange={(v) => setAmenities({ has_full_galley: v })} />
                    <Switch label="Enclosed lavatory" checked={form.amenities.has_enclosed_lavatory} onChange={(v) => setAmenities({ has_enclosed_lavatory: v })} />
                    <Switch label="Satellite phone" checked={form.amenities.has_satellite_phone} onChange={(v) => setAmenities({ has_satellite_phone: v })} />
                  </div>
                  <div className="mt-3">
                    <ChipInput
                      label="Additional Amenities"
                      values={form.amenities.additional_amenities}
                      onChange={(v) => setAmenities({ additional_amenities: v })}
                      placeholder="Add an amenity and press Enter"
                    />
                  </div>
                </section>

                {/* Description */}
                <section>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                  <div className="relative">
                    <textarea
                      rows={4}
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value.slice(0, DESCRIPTION_LIMIT) }))}
                      placeholder="Tell us more about the aircraft…"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                    <div className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
                      {form.description.length}/{DESCRIPTION_LIMIT}
                    </div>
                  </div>
                </section>

                {/* Photos */}
                <section>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Aircraft Photos <span className="text-primary-500">*</span>
                    </label>
                    {availableSuggestions.length > 0 && (
                      <button
                        type="button"
                        onClick={handleAddAllSuggestions}
                        className="text-xs text-primary-700 hover:text-primary-800 hover:underline"
                      >
                        Add all suggested ({availableSuggestions.length})
                      </button>
                    )}
                  </div>

                  {/* Suggested photos strip — shown when the picked
                      make/model has bundled fallback images in the app.
                      Click a thumb to append it as an 'existing' photo
                      (skips S3 upload, passes through into image_urls). */}
                  {availableSuggestions.length > 0 && (
                    <div className="mb-3 p-3 rounded-xl border border-primary-100 bg-primary-50/40">
                      <p className="text-xs text-gray-600 mb-2">
                        Suggested for <span className="font-medium text-gray-800">
                          {form.makeModel.manufacturer || form.makeModel.make_code} {form.makeModel.model_name || form.makeModel.model_code}
                        </span> — click to add.
                      </p>
                      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                        {availableSuggestions.map((url) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => handleAddSuggestion(url)}
                            className="flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border border-primary-200 hover:border-primary-500 hover:ring-2 hover:ring-primary-500 transition-all bg-white"
                            title="Add this suggested photo"
                            aria-label="Add suggested photo"
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef} type="file" accept="image/jpeg,image/png" multiple className="hidden"
                    onChange={(e) => { handlePickPhotos(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  />
                  <input
                    ref={addMoreInputRef} type="file" accept="image/jpeg,image/png" multiple className="hidden"
                    onChange={(e) => { handlePickPhotos(e.target.files); if (addMoreInputRef.current) addMoreInputRef.current.value = ''; }}
                  />
                  {form.photos.length === 0 ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border border-dashed border-gray-300 rounded-2xl px-4 py-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                    >
                      <div className="mx-auto w-10 h-10 flex items-center justify-center text-primary-500">
                        <RiUploadCloud2Line className="w-8 h-8" />
                      </div>
                      <div className="mt-2 text-sm font-medium text-gray-900">Choose files</div>
                      <div className="text-xs text-gray-500 mt-0.5">JPEG and PNG, up to {MAX_FILE_MB} MB each.</div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                        className="mt-4 px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-800 hover:bg-gray-50 transition-colors"
                      >
                        Browse Files
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {form.photos.map((photo, index) => (
                        <div key={photo.previewUrl} className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2.5">
                          <img src={photo.previewUrl} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{photo.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {(photo.size / (1024 * 1024)).toFixed(1)} MB ·{' '}
                              <span className="inline-flex items-center gap-0.5 text-green-600">
                                <RiCheckboxCircleFill className="w-3 h-3" /> Ready
                              </span>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove photo"
                          >
                            <RiDeleteBinLine className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {form.photos.length < MAX_PHOTOS && (
                        <button
                          type="button"
                          onClick={() => addMoreInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors"
                        >
                          <RiImageLine className="w-4 h-4" /> Add more images
                        </button>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>

          {/* Sticky footer — Back + Next / Preview (real flex child so it
              anchors to the bottom of the drawer regardless of content height). */}
          <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={handleBack}
              disabled={activeIndex === 0}
              className="flex-1 px-4 py-3 rounded-full border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <RiArrowLeftLine className="w-4 h-4" /> Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!currentTabValid}
              className="flex-1 px-4 py-3 rounded-full bg-primary-700 hover:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              {activeIndex === TABS.length - 1 ? 'Preview' : 'Next'}
              <RiArrowRightLine className="w-4 h-4" />
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Operator picker — rendered as SIBLING of Drawer so Vaul's outside-
          click handler doesn't treat the modal portal event as a drawer close. */}
      <SelectOperatorModal
        isOpen={operatorPickerOpen}
        onClose={() => setOperatorPickerOpen(false)}
        onSelect={(op) => setForm((p) => ({ ...p, operator: op }))}
      />

      {/* Make/Model search — same sibling pattern. */}
      <MakeModelSearchDialog
        isOpen={makeModelSearchOpen}
        onClose={() => setMakeModelSearchOpen(false)}
        onSelect={handleMakeModelPick}
      />

      {/* Preview — clone of TailDetailsDrawer reading form state. Save fires
          the 6-step save chain; retries stay on this stack. */}
      <AircraftPreviewDrawer
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        formState={form}
        onBack={() => setPreviewOpen(false)}
        onSave={handleSaveFromPreview}
        isSaving={isRunning}
        saveLabel={isEditMode ? 'Save changes' : 'Save'}
      />

      {/* Retry dialog — cancel keeps the wizard open per spec. */}
      <SaveRetryDialog
        error={saveError}
        isRunning={isRunning}
        onRetry={retry}
        onCancel={resetSave}
      />
    </>
  );
};

export default AddAircraftManuallyDrawer;
